/**
 * Promotional pieces that are not documents — a DTC video, a podcast, a social
 * post — are stored as section-less ``promotional`` letters whose ``source_url``
 * is the piece itself (``source = 'promo_video'`` for videos; written by
 * ``backend/scripts/add_promo_external_links.py``). There is nothing to render,
 * so the viewer offers a link that opens the piece in a NEW tab: the Precepi
 * page keeps its place, no deep link needed.
 * ponytail: link-out only; embed FDA-hosted MP4s with <video> when wanted.
 */

/** The piece's URL when this letter is an external promo, else null. Only
 *  absolute http(s) URLs: ``source_url`` lands in an href. */
export function externalPromoUrl(letter: {
  letter_type: string;
  sections: unknown[];
  source_url: string | null;
}): string | null {
  if (letter.letter_type !== 'promotional' || letter.sections.length > 0 || !letter.source_url) {
    return null;
  }
  try {
    const u = new URL(letter.source_url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;   // relative or malformed
  }
}

export default function PromoExternalLink({ url, isVideo }: { url: string; isVideo: boolean }) {
  return (
    <div className="mt-3 pt-3 border-t border-amber-200 flex flex-wrap items-center gap-3">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
      >
        {isVideo ? 'Watch video' : 'Open promotional piece'}
        <span aria-hidden="true">↗</span>
      </a>
      <span className="text-xs text-slate-500">Opens {new URL(url).hostname} in a new tab</span>
    </div>
  );
}
